import { useMemo } from "react";
import { Configurations } from "@/constants";
import { Maybe } from "@/types";
import { useApiUrl, useCustom, useTranslation } from "@refinedev/core";
import { getLanguageName } from "helpers/core";

type SystemConfigurationResponse = Record<string, string | string[] | number>;

export interface LanguageOption {
  label: string;
  value: string;
  description: string;
}

export const useSystemConfiguration = () => {
  const apiUrl = useApiUrl();
  const { getLocale } = useTranslation();
  const currentLanguage = getLocale();

  const { data, isLoading, isError } = useCustom<SystemConfigurationResponse>({
    url: `${apiUrl}/api/system/config/media`,
    method: "get",
    queryOptions: {
      staleTime: 5 * 60 * 1000, // 5minutes
    },
  });

  const supportedLanguages = useMemo(
    () => (data?.data?.[Configurations.SUPPORTED_LOCALES] as string[]) || [],
    [data]
  );

  const supportedLanguagesOptions: LanguageOption[] = useMemo(
    () =>
      supportedLanguages.map((locale) => {
        const languageName = getLanguageName(locale, currentLanguage, true);
        return {
          label: languageName ?? locale,
          value: locale,
          description: locale,
        };
      }),
    [supportedLanguages, currentLanguage]
  );
  const getConfiguration = <T>(key: string) => data?.data?.[key] as Maybe<T>;

  return {
    data: data?.data,
    getConfiguration,
    supportedLanguages,
    supportedLanguagesOptions,
    isLoading,
    isError,
  };
};

export const useConfigurationValue = <T>(key: string) => {
  const { getConfiguration } = useSystemConfiguration();
  return getConfiguration<T>(key);
};
